import { useModal } from '@klv/context/modal';
import { backgroundToTextColor } from '@klv/shared/styles/utils';
import { generateClassName } from '@klv/shared/utils/functions';

import { useHeroFormState } from './hooks/useHeroFormState';
import { useHeroColors } from './hooks/useHeroColors';
import { useHeroMedia } from './hooks/useHeroMedia';
import { useHeroLayout } from './hooks/useHeroLayout';
import HeroContentWrapper from './components/HeroContentWrapper';
import HeroContents from './components/HeroContents';
import HeroGrid from './components/HeroGrid';
import HeroMedia from './components/HeroMedia';
import HeroForms from './components/forms/HeroForms';

import * as styles from './styles/hero.module.scss';

import type { IHero } from './types';


const Hero = ({
  background = 'lightSand',
  backgroundJpeg,
  removeBottomPadding = false,
  demoFormHeader,
  demoFormRedirectUrl,
  embeddedForm = 'none',
  embeddedFormHeadline = '',
  embeddedFormBodyCopy = '',
  embeddedFormHeaderSubText = '',
  bodyCopy = '',
  buttons = [],
  eyebrow = '',
  eyebrowColor = 'defaultColor',
  eyebrowFont = 'defaultFont',
  eyebrowTag = 'div',
  formId = '',
  hideMediaOnMobile = false,
  hubspotRedirectUrl = '',
  hubspotHeaderText = '',
  hubspotSubheaderText = '',
  headline = '',
  image,
  imageLink,
  imageType = 'horizontal',
  largeText,
  loopingVideo,
  mediaType = 'image',
  poster,
  pricingVariant = false,
  rive,
  showAgencyFields = false,
  showSingleLineForm,
  singleLineForm,
  videoHeadline,
  videoUrl,
  videoDuration,
  videoOverlayTitle,
  videoPlayButtonColor,
  demoFormSettings,
  textAlignment = 'left',
}: IHero) => {
  const { formState, setFormState } = useHeroFormState();
  const { modalState } = useModal();
  const { cssBackgroundColor, backgroundColor } = useHeroColors(background);
  const { heroWithImage, hasMedia } = useHeroMedia({ image, mediaType, loopingVideo, poster, rive, videoUrl });
  const { layout, isTwoColumn, textAlign } = useHeroLayout(mediaType, imageType, embeddedForm, hasMedia, textAlignment);
  
  const hasForm = Boolean(formId);
  const hasContent = (headline || bodyCopy || buttons?.length > 0 || hasMedia) as boolean;

  return (
    <div 
      data-organism="hero"
      className={generateClassName([
        styles.hero,
        removeBottomPadding ? styles.removeBottomPadding : '',
        backgroundJpeg ? styles.backgroundJpeg : '',
        pricingVariant ? styles.pricingVariant : '',
      ])}
      style={{
        '--hero-background-color': backgroundJpeg ? 'transparent' : cssBackgroundColor,
        '--hero-text-color': backgroundToTextColor[background as keyof typeof backgroundToTextColor],
        '--hero-background-image': backgroundJpeg ? `url('/static/hero-gradients/${backgroundJpeg}${isTwoColumn ? '2Col' : ''}.webp'), linear-gradient(${cssBackgroundColor}, ${cssBackgroundColor})` : 'none',
      } as React.CSSProperties}
    >
              <HeroGrid embeddedForm={embeddedForm} hasForm={hasForm} hasImage={Boolean(mediaType === 'image' && heroWithImage || mediaType === 'rive')} hasMedia={hasMedia} imageType={layout} textAlignment={textAlignment}>
        {hasContent && (<HeroContentWrapper embeddedForm={embeddedForm}>
          <HeroContents
            eyebrow={eyebrow}
            eyebrowColor={eyebrowColor}
            eyebrowFont={eyebrowFont}
            eyebrowTag={eyebrowTag}
            headline={headline}
            bodyCopy={bodyCopy}
            largeText={largeText}
            embeddedForm={embeddedForm}
            hasForm={hasForm}
            formId={formId}
            buttons={buttons}
            background={background}
            showSingleLineForm={showSingleLineForm}
            singleLineForm={singleLineForm}
            textAlign={textAlign}
            mediaType={mediaType}
            rive={rive}
            hasMedia={hasMedia}
          />
          <HeroMedia
            hideMediaOnMobile={hideMediaOnMobile}
            mediaType={mediaType}
            image={image}
            imageLink={imageLink}
            loopingVideo={loopingVideo}
            poster={poster}
            rive={rive}
            videoDuration={videoDuration}
            videoHeadline={videoHeadline}
            videoUrl={videoUrl}
            videoPlayButtonColor={videoPlayButtonColor}
            videoOverlayTitle={videoOverlayTitle}
            background={background}
            heroWithImage={heroWithImage}
          />
          </HeroContentWrapper>
        )}
        <HeroForms
          embeddedForm={embeddedForm}
          modalState={modalState}
          hasForm={hasForm}
          formId={formId}
          background={background}
          backgroundColor={backgroundColor}
          embeddedFormBodyCopy={embeddedFormBodyCopy}
          embeddedFormHeadline={embeddedFormHeadline}
          embeddedFormHeaderSubText={embeddedFormHeaderSubText}
          hubspotRedirectUrl={hubspotRedirectUrl}
          hubspotSubheaderText={hubspotSubheaderText}
          hubspotHeaderText={hubspotHeaderText}
          demoFormHeader={demoFormHeader}
          demoFormRedirectUrl={demoFormRedirectUrl}
          demoFormSettings={demoFormSettings}
          formState={formState}
          setFormState={setFormState}
          showAgencyFields={showAgencyFields}
          hasContent={hasContent}
        />
      </HeroGrid>
    </div>
  );
};

export default Hero;
